import {
  IFieldArray,
  IFieldConfigurationObject,
  IFieldGroup,
} from "AngularFormly";
import {
  Field,
  ProcessApiResponse,
  WorkItemTypeConfig,
} from "../../model/ProcessApi";

import { vsWrapperName as labelWrapper } from "./wrappers/label";
import { vsWrapperName as childrenWrapper } from "./wrappers/children";
import { ProcessGroup } from "../../model/ProcessGroups";

export interface ProcessFieldInformation {
  fields: IFieldConfigurationObject[];
  fieldLookup: Map<string, IFieldConfigurationObject>;
}

export const vsServiceName = "workItemService";

interface ItemCommandHandler {
  (field: Field, commandName: string, commandArgument: any): void;
}

export class WorkItemService {
  static $inject = [];
  constructor(
    private readonly workItemTypes: Map<string, WorkItemTypeConfig>
  ) {}

  getFormFields(
    process: ProcessApiResponse<any>,
    processGroup: ProcessGroup,
    fields: Field[],
    itemCommandHandler: ItemCommandHandler
  ): ProcessFieldInformation {
    const lookup = new Map<string, IFieldConfigurationObject>();
    const fieldConfigurations = this.getFormFieldsInternal(
      process,
      processGroup,
      fields,
      itemCommandHandler,
      lookup
    );

    return {
      fields: fieldConfigurations,
      fieldLookup: lookup,
    };
  }

  private getFormFieldsInternal(
    process: ProcessApiResponse<any>,
    processGroup: ProcessGroup,
    fields: Field[],
    itemCommandHandler: ItemCommandHandler,
    fieldLookup: Map<string, IFieldConfigurationObject>
  ): IFieldConfigurationObject[] {
    const fieldConfigurations = (fields ?? []).map((f) => {
      const formlyField = this.getFormlyField(
        process,
        processGroup,
        f,
        itemCommandHandler
      );
      const data = formlyField.data ?? {};
      const childFields = this.getFormFieldsInternal(
        process,
        processGroup,
        f.Children ?? [],
        itemCommandHandler,
        fieldLookup
      );

      data.children = childFields;
      fieldLookup.set(f.WorkItemId, formlyField);

      return formlyField;
    });

    return fieldConfigurations;
  }

  doLayout(
    fieldConfig: IFieldConfigurationObject[],
    defaultSize?: number
  ): IFieldGroup[] {
    let currentItems: IFieldArray = [];
    let sizeAccumulator = 0;
    const fieldGroups: IFieldGroup[] = [];

    defaultSize = defaultSize === undefined ? 2 : Math.round(defaultSize);

    fieldConfig.forEach((formlyField) => {
      const data = formlyField.data ?? {};
      const size = (data.size ??= defaultSize);
      if (size + sizeAccumulator > 4) {
        fieldGroups.push({
          fieldGroup: currentItems,
          className: "row",
        });
        currentItems = [];
        sizeAccumulator = 0;
      }
      formlyField.className = `col-xs-${size * 3}`;

      currentItems.push(formlyField);
      sizeAccumulator += size;
    });
    if (currentItems.length > 0) {
      fieldGroups.push({
        fieldGroup: currentItems,
        className: "row",
      });
    }
    return fieldGroups;
  }

  getFormlyField(
    process: ProcessApiResponse<any>,
    processGroup: ProcessGroup,
    field: Field,
    itemCommandHandler: ItemCommandHandler
  ): IFieldConfigurationObject {
    if (!this.workItemTypes.has(field.Type)) {
      throw new Error(`Unrecognized field type: ${field.Type}`);
    }

    const config = this.workItemTypes.get(field.Type)!;

    const fieldConfiguration: IFieldConfigurationObject = {
      id: field.WorkItemId,
      type: config.formlyFieldType,
      wrapper: [labelWrapper, childrenWrapper],
    };

    this.configureField(fieldConfiguration, field);
    const data = fieldConfiguration.data!;
    data.processGroup = processGroup;
    data.itemCommandHandler = itemCommandHandler;
    data.process = process;
    data.size = config.size;

    config.customizer?.call(undefined, field);

    return fieldConfiguration;
  }

  configureField(formlyFieldConfig: IFieldConfigurationObject, field: Field) {
    const model: any = (formlyFieldConfig.model ??= {});

    const options = field.Options;

    if (!formlyFieldConfig.templateOptions) {
      formlyFieldConfig.templateOptions = { ...options };

      const expressionProperties = (formlyFieldConfig.expressionProperties ??=
        {});

      for (const propertyName in options) {
        expressionProperties[
          `templateOptions.${propertyName}`
        ] = `options.data.options.${propertyName}`;
      }
    }

    //formlyFieldConfig.templateOptions = { ...options };
    model.value = field.Value;
    formlyFieldConfig.data = { ...formlyFieldConfig.data, field, options };
  }
}
